import { getSupabaseClient } from "@/lib/supabase/client";

export interface LnatResource {
    id: string;
    resource_name: string;
    provider: string | null;
    resource_type: string | null;
    description: string | null;
    access_url: string | null;
    usage_notes: string | null;
    copyright_status: string | null;
    recommended_use_stage: string | null;
    last_verified_date: string | null;
    citation_url: string | null;
    created_at: string;
    updated_at: string;
}

export interface LnatMistake {
    id: string;
    section: string | null;
    skill_tag: string | null;
    mistake_title: string;
    mistake_description: string | null;
    why_students_fall_for_it: string | null;
    correction_strategy: string | null;
    difficulty_level: string | null;
    applicable_question_types: string | null;
    volatility_level: string | null;
    last_verified_date: string | null;
    citation_url: string | null;
    created_at: string;
    updated_at: string;
}

export interface LnatEssayTemplate {
    id: string;
    template_name: string;
    use_case: string | null;
    time_allocation_minutes: number | null;
    structure_outline: string | null;
    thesis_guidance: string | null;
    paragraph_blueprint: string | null;
    counterargument_pattern: string | null;
    common_pitfalls: string | null;
    aligned_rubric_criteria: string | null;
    ethics_note: string | null;
    volatility_level: string | null;
    last_verified_date: string | null;
    citation_url: string | null;
    created_at: string;
    updated_at: string;
}

export interface LnatUniversityScoreReport {
    id: string;
    university_name: string;
    country: string | null;
    lnats_required: boolean;
    score_type: string | null;
    score_range_or_statement: string | null;
    source_context: string | null;
    admissions_usage_note: string | null;
    data_year: string | null;
    volatility_level: string | null;
    last_verified_date: string | null;
    citation_url: string | null;
    disclaimer_required: boolean;
    created_at: string;
    updated_at: string;
}

export interface LnatPracticeSession {
    id: string;
    user_id: string;
    section: string | null;
    difficulty: string | null;
    question_count: number | null;
    correct_count: number | null;
    accuracy_percent: number | null;
    duration_minutes: number | null;
    notes: string | null;
    created_at: string;
}

export interface LnatMistakeLog {
    id: string;
    user_id: string;
    mistake_id: string | null;
    section: string | null;
    mistake_title: string;
    how_to_fix: string | null;
    notes: string | null;
    created_at: string;
}

export interface LnatEssaySubmission {
    id: string;
    user_id: string;
    template_id: string | null;
    prompt_topic: string | null;
    checklist: string[];
    checklist_completed: number;
    word_count: number | null;
    self_rating: number | null;
    notes: string | null;
    created_at: string;
}

export interface CreateLnatPracticeSessionInput {
    section: string;
    difficulty?: string | null;
    question_count?: number | null;
    correct_count?: number | null;
    accuracy_percent?: number | null;
    duration_minutes?: number | null;
    notes?: string | null;
}

export interface CreateLnatMistakeLogInput {
    mistake_id?: string | null;
    section?: string | null;
    mistake_title: string;
    how_to_fix?: string | null;
    notes?: string | null;
}

export interface CreateLnatEssaySubmissionInput {
    template_id?: string | null;
    prompt_topic?: string | null;
    checklist: string[];
    checklist_completed: number;
    word_count?: number | null;
    self_rating?: number | null;
    notes?: string | null;
}

/**
 * Fetches all official LNAT resources.
 */
export async function getLnatResources(): Promise<LnatResource[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_official_resources")
        .select("*")
        .order("resource_name", { ascending: true });

    if (error) {
        console.error("Error fetching LNAT resources:", error);
        return [];
    }
    return data || [];
}

/**
 * Fetches LNAT common mistakes with optional filters.
 */
export async function getLnatMistakes(filters?: {
    section?: string;
    skill_tag?: string;
}): Promise<LnatMistake[]> {
    const supabase = getSupabaseClient();
    let query = supabase.from("lnat_mistakes").select("*");

    if (filters?.section) {
        query = query.eq("section", filters.section);
    }
    if (filters?.skill_tag) {
        query = query.eq("skill_tag", filters.skill_tag);
    }

    const { data, error } = await query.order("id", { ascending: true });

    if (error) {
        console.error("Error fetching LNAT mistakes:", error);
        return [];
    }
    return data || [];
}

/**
 * Fetches LNAT essay templates.
 */
export async function getLnatEssayTemplates(): Promise<LnatEssayTemplate[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_essay_templates")
        .select("*")
        .order("id", { ascending: true });

    if (error) {
        console.error("Error fetching LNAT essay templates:", error);
        return [];
    }
    return data || [];
}

/**
 * Fetches LNAT university score reports.
 */
export async function getLnatUniversityScoreReports(
    _universityName?: string
): Promise<LnatUniversityScoreReport[]> {
    // Withdraw the audited, unreliable dataset from both member views.
    // Restore only after a sourced, year-specific reconciliation; preserve the
    // stored rows for audit rather than silently relabelling them as verified.
    return [];
}

export async function getUserLnatPracticeSessions(
    userId: string
): Promise<LnatPracticeSession[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_practice_sessions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

    if (error) {
        console.error("Error fetching LNAT practice sessions:", error);
        return [];
    }
    return (data as LnatPracticeSession[]) || [];
}

export async function createLnatPracticeSession(
    userId: string,
    payload: CreateLnatPracticeSessionInput
): Promise<LnatPracticeSession | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_practice_sessions")
        .insert({
            user_id: userId,
            section: payload.section,
            difficulty: payload.difficulty ?? null,
            question_count: payload.question_count ?? null,
            correct_count: payload.correct_count ?? null,
            accuracy_percent: payload.accuracy_percent ?? null,
            duration_minutes: payload.duration_minutes ?? null,
            notes: payload.notes ?? null,
        })
        .select("*")
        .single();

    if (error) {
        console.error("Error creating LNAT practice session:", error);
        return null;
    }
    return data as LnatPracticeSession;
}

export async function getUserLnatMistakeLogs(
    userId: string
): Promise<LnatMistakeLog[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_mistake_logs")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

    if (error) {
        console.error("Error fetching LNAT mistake logs:", error);
        return [];
    }
    return (data as LnatMistakeLog[]) || [];
}

export async function createLnatMistakeLog(
    userId: string,
    payload: CreateLnatMistakeLogInput
): Promise<LnatMistakeLog | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_mistake_logs")
        .insert({
            user_id: userId,
            mistake_id: payload.mistake_id ?? null,
            section: payload.section ?? null,
            mistake_title: payload.mistake_title,
            how_to_fix: payload.how_to_fix ?? null,
            notes: payload.notes ?? null,
        })
        .select("*")
        .single();

    if (error) {
        console.error("Error creating LNAT mistake log:", error);
        return null;
    }
    return data as LnatMistakeLog;
}

export async function getUserLnatEssaySubmissions(
    userId: string
): Promise<LnatEssaySubmission[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_essay_submissions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

    if (error) {
        console.error("Error fetching LNAT essay submissions:", error);
        return [];
    }
    return (data as LnatEssaySubmission[]) || [];
}

export async function createLnatEssaySubmission(
    userId: string,
    payload: CreateLnatEssaySubmissionInput
): Promise<LnatEssaySubmission | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("lnat_essay_submissions")
        .insert({
            user_id: userId,
            template_id: payload.template_id ?? null,
            prompt_topic: payload.prompt_topic ?? null,
            checklist: payload.checklist,
            checklist_completed: payload.checklist_completed,
            word_count: payload.word_count ?? null,
            self_rating: payload.self_rating ?? null,
            notes: payload.notes ?? null,
        })
        .select("*")
        .single();

    if (error) {
        console.error("Error creating LNAT essay submission:", error);
        return null;
    }
    return data as LnatEssaySubmission;
}
